import { DataTable, Number, Tooltip } from "components/common"
import MonthPicker from "components/common/monthPicker/monthPicker"
import { DATE_FORMAT, YYYY_MM_DD_FORMAT } from "constants"
import { SALES_PIPELINE } from "constants/app-paths"
import dayjs from "dayjs"
import { useLazyGetProfileSummaryQuery } from "features/shared/api"
import { t } from "i18next"
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { convertToReadableString } from "utils"
import { dateTimeFunction } from "utils/date"
import styles from "./index.module.less"

const PipelineTable = ({ staffId }) => {
  const columns = () => [
    { name: t("STAGE"), dataIndex: "stage", width: 100 },
    { name: t("LEADS"), dataIndex: "leads", width: 75 },
    { name: t("PROBABILITY"), dataIndex: "probability", width: 100 },
    {
      name: t("POT. REV"),
      dataIndex: "potentialRevenue",
      tooltip: t("POTENTIAL REVENUE"),
      width: 150,
    },
    {
      name: t("PROB. REV"),
      dataIndex: "probableRevenue",
      tooltip: t("PROBABLE REVENUE"),
      width: 150,
    },
    {
      name: t("REP. REV"),
      dataIndex: "reportedRevenue",
      tooltip: t("REPORTED REVENUE"),
      width: 150,
    },
  ]

  const tableColumns = columns()?.map(col => ({
    ...col,
    title: (
      <Tooltip placement={"topLeft"} title={col?.tooltip}>
        {col?.name}
      </Tooltip>
    ),
  }))

  const [getProfileSummary, { data, isFetching, error }] = useLazyGetProfileSummaryQuery()
  const [date, setDate] = useState(null)
  useEffect(() => {
    getProfileSummary({ staffId, load: "pipeline" }, true)
  }, [])
  const tableData = useMemo(() => {
    if (data) {
      return Object.keys(data?.pipeline).map((item, index) => {
        return {
          key: item,
          stage: t(convertToReadableString(item)),
          leads:
            data?.pipeline?.[item]?.leads > 0 ? (
              <Link
                to={`${SALES_PIPELINE}?allocatedTo=${staffId}&month=${
                  date ? dayjs(decodeURIComponent(date)).format(YYYY_MM_DD_FORMAT) : dayjs().format(YYYY_MM_DD_FORMAT)
                }`}
                target={"_blank"}
              >
                {data?.pipeline?.[item]?.leads}
              </Link>
            ) : (
              data?.pipeline?.[item]?.leads
            ),
          potentialRevenue: data?.pipeline?.[item]?.revenue > 0 ? <Number digitAfterFraction={2} value={data?.pipeline?.[item]?.revenue} tooltip={true} /> : "--",
          probability: ( data?.pipeline?.[item]?.probability > 0 ? 
            <Number digitAfterFraction={2} value={data?.pipeline?.[item]?.probability} suffix={"%"} tooltip={true} /> : "--"
          ),
          probableRevenue: ( data?.pipeline?.[item]?.probableRevenue > 0 ? 
            <Number digitAfterFraction={2} value={data?.pipeline?.[item]?.probableRevenue} tooltip={true} /> : "--"
          ),
          reportedRevenue: ( data?.pipeline?.[item]?.reportedRevenue > 0 ? 
            <Number digitAfterFraction={2} value={data?.pipeline?.[item]?.reportedRevenue} tooltip={true} /> : "--"
          ),
        }
      })
    }
    return []
  }, [data])

  const handleOnDateChange = date => {
    setDate(date)
    const toDate = encodeURIComponent(
      dateTimeFunction({ date: dayjs(date)?.endOf("day"), apiRequest: true, timeZone: true })
    )
    getProfileSummary({ staffId, load: "pipeline", params: { toDate: toDate } }, true)
  }

  return (
    <>
      <MonthPicker
        onChange={handleOnDateChange}
        format={DATE_FORMAT}
        showTime={false}
        value={date ? dayjs(decodeURIComponent(date)) : dayjs()}
        disabledDate={date => {
          return date && dayjs(date)?.isAfter(dayjs(), "day")
        }}
        style={{
          maxWidth: 180,
          marginBottom: "10px",
        }}
        allowClear={false}
      />
      <DataTable
        className={styles.pipelineTable}
        stickyOffset={-16}
        error={error}
        data={tableData || []}
        noUrlPush={true}
        loading={isFetching}
        columns={tableColumns || []}
      />
    </>
  )
}
export default PipelineTable
